import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Santa Clara", cities: "Santa Clara, Sunnyvale, Cupertino, Mountain View" },
    { zone: "San José", cities: "San José, Willow Glen, Almaden, Evergreen" },
    { zone: "Palo Alto", cities: "Palo Alto, Los Altos, Los Altos Hills, Menlo Park" },
    { zone: "Sur del Condado", cities: "Los Gatos, Saratoga, Campbell, Morgan Hill" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos en el Condado de Santa Clara</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en todo Silicon Valley y sus alrededores.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Santa Clara</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const SantaClaraSpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "santa-clara", name: "Santa Clara", type: "city", regionSlug: "santa-clara" }}>
      <SEO
        title="Limpieza del Hogar en Santa Clara, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en Santa Clara, Sunnyvale, Cupertino, Mountain View y todo el Condado de Santa Clara. Precios planos, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar santa clara, servicio de limpieza santa clara ca, limpieza profunda santa clara, empleada doméstica santa clara, maid service silicon valley español, limpieza sunnyvale cupertino mountain view, servicio de limpieza condado santa clara"
        url="https://uplevelcleaning.com/es/santa-clara"
        locationName="Santa Clara"
        regionSlug="santa-clara"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Santa Clara", url: "/es/santa-clara" }
        ]}
        reviews={[
          { author: "Marcela S.", rating: 5, reviewBody: "Trabajo en una empresa tecnológica en Santa Clara y el tiempo es oro. Uplevel Cleaning ha sido la mejor solución — llegan puntual, limpian perfectamente y el precio siempre es exacto.", datePublished: "2026-03-18" },
          { author: "Ramón V.", rating: 5, reviewBody: "Contraté el servicio quincenal para nuestra casa en Sunnyvale y ha sido una de las mejores decisiones del año. El equipo es muy profesional y respetuoso.", datePublished: "2026-02-20" },
          { author: "Diana C.", rating: 5, reviewBody: "Vivo en Cupertino y buscaba un servicio de limpieza de confianza. ¡Uplevel superó todas mis expectativas! La casa siempre queda perfecta.", datePublished: "2026-01-30" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="Santa Clara" regionSlug="santa-clara" />

          {/* Local trust bar */}
          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Servicio Local en Santa Clara",
                "Silicon Valley y Alrededores",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="Santa Clara" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="santa-clara" />
          <Guarantee />
          <SpanishFAQ cityName="Santa Clara" />
          <InternalLinks />
          <SpanishFinalCTA cityName="Santa Clara" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default SantaClaraSpanishPage;
